import { isNotDefined } from '@sgme/fp';
import { VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { BulkCreationTable } from '@/App/BulkTrade/BulkCreationTable.tsx';
import { BulkError } from '@/App/BulkTrade/BulkError.tsx';
import { BulkPriceTable } from '@/App/BulkTrade/BulkPriceTable.tsx';
import { BulkToolbar } from '@/App/BulkTrade/BulkToolbar.tsx';
import {
  BulkTradeProvider,
  useBulkTradeTradeCaptureError,
  useResetBulkTradeTradeCaptureError,
} from '@/App/BulkTrade/BulkTradeContext.tsx';
import { ErrorTile } from '@/App/tiles/shared/ErrorTile.tsx';
import Stepper from '@/components/ui/Stepper';
import { selectBulkTrade } from '@/store/bulkTrade/selectors.ts';
import { useAppSelector } from '@/store/hooks.ts';

export type BulkTradeProps = {
  tabId: string;
};

export const BulkTrade = (props: BulkTradeProps) => {
  return (
    <BulkTradeProvider tabId={props.tabId}>
      <BulkTradeInner tabId={props.tabId} />
    </BulkTradeProvider>
  );
};

function BulkTradeInner(props: BulkTradeProps) {
  const { tabId } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  useEffect(() => {
    sgwtWebAnalytics?.trackEvent('BulkTrade', 'opened', tabId);
  }, [tabId, sgwtWebAnalytics]);

  const tradeCaptureError = useBulkTradeTradeCaptureError();
  const resetTradeCaptureError = useResetBulkTradeTradeCaptureError();

  const currentStepIndex = useAppSelector((state) => {
    const bulkTrade = selectBulkTrade(state, tabId);

    return isNotDefined(bulkTrade) || bulkTrade.step === 'building'
      ? (0 as const)
      : bulkTrade.step === 'priced'
        ? (1 as const)
        : (2 as const);
  });

  if (tradeCaptureError !== undefined) {
    return (
      <ErrorTile
        tileId={tabId}
        onReset={resetTradeCaptureError}
        errorCode={tradeCaptureError.errorCode}
        errorTitle="bulkTrade.error.title"
        errorDescription="bulkTrade.error.description"
      />
    );
  }

  return (
    <VStack verticalAlignment="start" fullWidth fullHeight gap="8px" padding="24px">
      <h3>
        <FormattedMessage id="bulkTrade.title" />
      </h3>

      <Stepper steps={ALL_STEPS} currentStepIndex={currentStepIndex} lastActiveStepIndex={currentStepIndex} />

      <BulkError tabId={tabId} />

      <BulkToolbar tabId={tabId} currentStepIndex={currentStepIndex} />

      {currentStepIndex === 0 && <BulkCreationTable tabId={tabId} />}

      {(currentStepIndex === 1 || currentStepIndex === 2) && <BulkPriceTable tabId={tabId} />}
    </VStack>
  );
}

const ALL_STEPS = [
  { labelId: 'bulkTrade.step1' as const },
  { labelId: 'bulkTrade.step2' as const },
  { labelId: 'bulkTrade.step3' as const },
];
